import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackIcon from '../../../assets/svg/backArrow.svg';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import MainContent from '../../../generic-components/Layout/MainContent';
import { CategorizeContainer } from '../../categorizeTransacrions/CategorizeContainer';
import { WIDGET_STYLES } from '../../dashboard/constants';
import SubContent from '../../../generic-components/Layout/SubContent';
import TransactionDetails, { CategorizationModes } from '../../transactions/components/TransactionDetails';
import { getDetailsComponentsToShow, getExtendedTransaction } from '../../transactions/utils';
import { ITransaction, ITransactionDetailsMap } from '../../../types';
import { useStore } from '../../../store';
import Heading from '../../../generic-components/Heading';
import { VIEWS_MAP } from '../../../router/constants';
import { useGetUncategorizedTotalNumOfRecords } from '../../../hooks/query/useGetUncategorizedTransactionsDataForScheduleCQuery';
import { Layout } from '../../../components/Layout/Layout';
import Button from '../../../generic-components/Button';
import Icon from '../../../generic-components/Icon';
import { featureAccess, LimitedFeatureType } from '../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../constants/general';
import { FeatureAccess } from '../../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';

export const Categorize = () => {
  const [activeTrx, setActiveTrx] = useState<ITransaction | null>(null);
  const [extendedTrx, setExtendedTrx] = useState<ITransaction | null>(null);
  const [detailsComponentsMap, setDetailsComponentsMap] = useState<ITransactionDetailsMap | null>(null);
  const [toggleData, setToggleData] = useState<{ incomeTrxCnt: number; expenseTrxCnt: number }>();
  const navigate = useNavigate();
  const { customerId } = useStore((state) => state.user);
  const { getPrimaryAccount } = useStore((state) => state);
  const { state: locationState } = useLocation();
  const { t } = useTranslation();
  const categorizePermission = featureAccess(
    FEATURE_FLAG.CATEGORIZATION.FEATURE,
    LimitedFeatureType.TransactionListCategorize,
  );
  const location = useLocation();

  const getQueryStr = (type: string) => {
    if (type === 'INCOME' && location.pathname.includes(VIEWS_MAP.EXPENSES)) return '';
    return `&startTime=${locationState.startDate.split('T')[0]}&endTime=${locationState.endDate.split('T')[0]}&categorizationType=${type}`;
  };
  const { mutateAsync: getUncategorizedTrxCount } = useGetUncategorizedTotalNumOfRecords();

  function getToggleData() {
    Promise.all([
      getUncategorizedTrxCount({ q: getQueryStr('INCOME'), customerId }),
      getUncategorizedTrxCount({ q: getQueryStr('EXPENSE'), customerId }),
    ]).then((values: [number, number]) => {
      const incomeTrxCnt = values[0];
      const expenseTrxCnt = values[1];
      if (!(incomeTrxCnt + expenseTrxCnt)) {
        setExtendedTrx(null);
      }
      setToggleData({ incomeTrxCnt, expenseTrxCnt });
    });
  }

  const handleActiveTrx = (data: ITransaction) => {
    setActiveTrx(data);
  };

  const enrichActiveTrx = async () => {
    if (!activeTrx) return;
    getExtendedTransaction({
      activeTransaction: activeTrx,
      customerId,
      accountNo: getPrimaryAccount().achAccountNumber,
    }).then((enrichedTrx) => {
      setExtendedTrx(enrichedTrx);
    });
  };

  useEffect(() => {
    if (!activeTrx) return;
    const map = getDetailsComponentsToShow(activeTrx);
    setDetailsComponentsMap(map);
    enrichActiveTrx();
  }, [activeTrx]);

  useEffect(() => {
    getToggleData();
  }, []);

  const handleFinishCategorize = () => {
    setExtendedTrx(null);
  };

  return (
    <Layout wrapperClassName='pt-[78px] pl-[72px] w-screen pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <div className='flex items-center mb-[24px]'>
        <Button
          onClick={() => navigate(`/${VIEWS_MAP.EXPENSES}`)}
          className=''
          id='iconbackButtonToExpenses'
          childElement={<Icon src={BackIcon} className='w-[10px] mr-[18px]' />}
        />
        <Heading title={t('CATEGORIZATION_VIEW_HEADER')} className='font-metricRSemibold text-bw08 text-[20px]' />
      </div>
      <ContentWrapper>
        <MainContent className='relative flex flex-col items-center justify-center bg-bw00 rounded-lg w-fit h-fit overflow-hidden'>
          {locationState?.timeFrameTitle && (
            <div className='flex flex-col w-full items-center justify-center text-center w-[500px] px-[20px] pt-[40px]'>
              <Heading
                id='timeframeHeader'
                title={`${t('CATEGORIZATION_MAIN_TITLE_PRE')} ${locationState.timeFrameTitle} ${t('CATEGORIZATION_MAIN_TITLE_SUFFIX')}`}
                className='font-metricMedium text-[16px] text-bw08 mb-[12px]'
              />
              <Heading
                id='timeframeSubHeader'
                title={`${t('CATEGORIZATION_SUB_CONTENT_PRE')} ${locationState.timeFrameTitle} ${t('CATEGORIZATION_SUB_CONTENT_SUFFIX')}`}
                className='font-metric text-[14px] text-bw06 mb-[10px]'
              />
            </div>
          )}
          <div className='w-[500px]'>
            {toggleData && (
              <CategorizeContainer
                isReadOnlyMode={categorizePermission !== FeatureAccess.READ_WRITE}
                toggleData={toggleData}
                filterRange={locationState}
                shouldHideHeader={locationState?.showHeader ? false : locationState?.timeFrameTitle}
                handleFinishCategorize={handleFinishCategorize}
                customMode='EXPENSE_REPORT'
                containerClassName={WIDGET_STYLES.CATEGORIZATION.WRAPPER}
                navSwitchClassName={WIDGET_STYLES.CATEGORIZATION.NAV_SWITCH_WRAPPER}
                onActiveTrxChange={handleActiveTrx}
                finishProcessCtaTitle={locationState?.backCtaTitle}
              />
            )}
          </div>
        </MainContent>
        {extendedTrx && (
          <SubContent className='bg-bw00 relative rounded-lg p-10 h-min tablet:w-[300px] pc-sm:w-[360px] overflow-y-hidden'>
            <TransactionDetails
              isReadOnly={categorizePermission !== FeatureAccess.READ_WRITE}
              detailsComponentsMap={detailsComponentsMap}
              detailsDataObject={extendedTrx}
              categorizationMode={CategorizationModes.TRIGGER}
            />
          </SubContent>
        )}
      </ContentWrapper>
    </Layout>
  );
};
